import React from "react";
import { ExternalLink } from "lucide-react";

import type { SectorPanelData } from "./HotTopicsSection.types";
import { formatPublishTime, formatTickers } from "./HotTopicsSection.utils";

export function SectorNewsPanel({
  panel,
  loading,
}: {
  panel?: SectorPanelData;
  loading?: boolean;
}) {
  if (loading && !panel) {
    return (
      <div className="h-[360px] rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="h-4 w-40 rounded bg-white/10" />
        <div className="mt-3 h-3 w-56 rounded bg-white/10" />
        <div className="mt-5 space-y-3">
          <div className="h-12 rounded-xl bg-white/5" />
          <div className="h-12 rounded-xl bg-white/5" />
          <div className="h-12 rounded-xl bg-white/5" />
        </div>
      </div>
    );
  }

  const safe = panel;
  if (!safe) return null;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-[0_18px_60px_-44px_rgba(0,0,0,0.9)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{safe.label}</div>
          <div className="mt-1 text-[11px] text-white/55">Top {safe.count} news</div>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        <div className="rounded-lg border border-rose-400/15 bg-rose-400/10 p-3">
          <div className="text-[11px] font-semibold text-rose-200">Bearish:</div>
          <div className="mt-1 text-[11px] leading-relaxed text-white/60">
            {formatTickers(safe.bearish)}
          </div>
        </div>

        <div className="rounded-lg border border-emerald-400/15 bg-emerald-400/10 p-3">
          <div className="text-[11px] font-semibold text-emerald-200">Bullish:</div>
          <div className="mt-1 text-[11px] leading-relaxed text-white/60">
            {formatTickers(safe.bullish)}
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {safe.news.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-white/60">
            No news available.
          </div>
        ) : (
          safe.news.map((n, idx) => (
            <a
              key={`${safe.sectorId}-${idx}-${n.url}`}
              href={n.url}
              target="_blank"
              rel="noreferrer noopener"
              className="group block rounded-xl border border-white/10 bg-white/5 p-3 transition hover:bg-white/8"
              aria-label={n.title}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] text-white/55">{formatPublishTime(n.publish_time)}</div>
                  <div className="mt-1 line-clamp-2 text-sm font-medium text-white/85">
                    {n.title}
                  </div>
                </div>
                <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-white/45 transition group-hover:text-white/70" />
              </div>

              {n.summary ? (
                <div className="mt-2 line-clamp-3 text-xs text-white/65">{n.summary}</div>
              ) : null}
            </a>
          ))
        )}
      </div>
    </div>
  );
}
